import React from 'react'
import { Layout, Typography, Button, Collapse } from 'antd'
import {
  Check,
  ArrowLeft,
  ArrowRight,
  FileSpreadsheet,
  FileText,
  PaintBucket,
  Users,
  DollarSign,
  ClipboardList,
  Link2,
  Truck
} from 'lucide-react'
import { SprintSpecLogo } from '../../components/Logo/SprintSpecLogo'
import { useNavigate } from 'react-router-dom'
import { marketingSlogans, usePageTitle } from '../../utils/pageTitle'
import { mapCountryToGeo, pricingByGeo } from '../../data/pricing'
import { useCompanyStore } from '../../store/company/companyStore'
import { CountryCode } from 'countries-and-timezones'
import { useApi } from '../../services/ApiContext'

const { Content } = Layout
const { Title, Text } = Typography

const features = [
  'Unlimited FF&E schedules',
  'Finishes & Accessories',
  'Custom branding',
  'Purchase Orders',
  'Client Approval Tracking',
  'Procurement Tracking',
  'Accounting Integration (Xero)',
  'Unlimited team member accounts',
  'Free Support & Training'
]

const featureHighlights = [
  {
    icon: <FileSpreadsheet className="h-6 w-6" />,
    title: 'FF&E Schedules',
    description:
      'Create detailed, cloud-based schedules for furniture, finishes, fixtures - anything your project needs.'
  },
  {
    icon: <PaintBucket className="h-6 w-6" />,
    title: 'Finishes Library',
    description:
      'Store specs, pricing, and supplier details for all your finishes in one curated space.'
  },
  {
    icon: <FileText className="h-6 w-6" />,
    title: 'Purchase Orders',
    description:
      'Auto-generate professional POs by supplier with item codes, quantities, and pricing.'
  },
  {
    icon: <DollarSign className="h-6 w-6" />,
    title: 'Cost Control',
    description:
      'Track costs, set margins, and manage pricing for items and materials across projects.'
  },
  {
    icon: <Users className="h-6 w-6" />,
    title: 'Client Approvals',
    description:
      'Keep client approvals organized, track decisions, and avoid endless email chains.'
  },
  {
    icon: <ClipboardList className="h-6 w-6" />,
    title: 'Project Management',
    description:
      'Plan tasks, deadlines, and phases in one place, using boards or timelines for clarity.'
  },
  {
    icon: <Link2 className="h-6 w-6" />,
    title: 'Accounting Sync',
    description:
      'Seamlessly integrate with Xero or QuickBooks - no more double data entry.'
  },
  {
    icon: <Truck className="h-6 w-6" />,
    title: 'Delivery Tracking',
    description:
      'Calculate delivery, installation, and cleanup costs and track status by supplier.'
  }
]

const faqs = [
  {
    question: 'Will I lose my existing data if I upgrade?',
    answer:
      'No, all your existing data will be preserved when you upgrade. You\'ll simply gain access to additional features and capabilities.'
  },
  {
    question: 'Can I cancel at any time?',
    answer:
      'Absolutely. There are no long-term contracts. You can cancel your subscription at any time and your data remains accessible until the end of your billing period.'
  },
  {
    question: 'What happens to my data if I cancel?',
    answer:
      'Your data is safely stored and accessible for 90 days after cancellation. You can export everything as PDF or CSV before your access ends.'
  },
  {
    question: 'How does team billing work?',
    answer:
      'Your subscription covers your entire team - unlimited team member accounts are included. There are no per-seat charges.'
  },
  {
    question: 'Can I switch between monthly and yearly?',
    answer:
      'Yes. You can switch to yearly billing at any time and receive a prorated credit for your remaining monthly period. Switching to monthly takes effect at your next renewal.'
  },
  {
    question: 'Do you offer onboarding support?',
    answer:
      'Yes, free onboarding support is included. We can help you migrate existing projects from spreadsheets or other tools. Most studios are fully set up within a day.'
  }
]

const ValuePropCard = ({
  title,
  description
}: {
  title: string
  description: string
}) => (
  <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm transition-all hover:shadow-md">
    <Text strong className="mb-2 block text-base">
      {title}
    </Text>
    <Text className="text-sm text-gray-500">{description}</Text>
  </div>
)

export const UpgradePage: React.FC = () => {
  const navigate = useNavigate()
  usePageTitle(marketingSlogans[0])

  const company = useCompanyStore((state) => state.company)
  const api = useApi()

  const pricing = pricingByGeo[mapCountryToGeo(company.country as CountryCode)]

  return (
    <Layout className="min-h-screen">
      <Content className="bg-white">
        {/* Hero */}
        <div className="relative overflow-hidden border-b border-gray-200 bg-gradient-to-b from-gray-50 to-white">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-40 left-1/3 h-[500px] w-[500px] rounded-full bg-primary/[0.04] blur-3xl" />
            <div className="absolute -bottom-20 right-1/4 h-[400px] w-[400px] rounded-full bg-primary/[0.03] blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-4xl px-6 pt-10 text-center pb-10">
            <Button
              onClick={() => navigate(-1)}
              className="absolute left-6 top-10"
              type="text"
              icon={<ArrowLeft className="w-4 h-4" />}
            />
            <SprintSpecLogo className="mb-6" size="large" />
            <Title
              level={1}
              className="text-4xl md:text-5xl font-bold mb-6 leading-tight"
            >
              Speed Up Your Studio's Workflow
            </Title>
            <Text className="text-xl text-gray-500 block max-w-2xl mx-auto mb-4">
              Take your design documentation to the next level with our premium
              features. Join design professionals who have upgraded to work
              faster and deliver better results.
            </Text>
          </div>
        </div>

        {/* Why Studios Choose SprintSpec */}
        <div className="bg-white py-16">
          <div className="mx-auto max-w-5xl px-6">
            <div className="mb-12 text-center">
              <Title level={2} className="mb-4 text-3xl font-bold">
                Why Studios Choose SprintSpec
              </Title>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <ValuePropCard
                title="Replaces 3+ Tools"
                description="Most studios previously paid for separate project management, spreadsheet templates, and document tools. SprintSpec consolidates everything into one purpose-built platform."
              />
              <ValuePropCard
                title="Pays for Itself in 1 Project"
                description="Studios save 6–10 hours per project on admin tasks. At $150/hour, that's $900–1,500 per project in time saved or extra capacity."
              />
              <ValuePropCard
                title="No Complicated Setup"
                description="Unlike generic PM tools, SprintSpec is purpose-built for interior designers. Your team can onboard in a day, not weeks."
              />
              <ValuePropCard
                title="Migrate from Anything"
                description="Import your existing projects from spreadsheets, other FF&E tools, or email chaos. Most studios migrate their active projects in 1–2 days."
              />
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-gray-50 py-16">
          <div className="mx-auto max-w-4xl px-6">
            <div className="mb-12 text-center">
              <Title level={2} className="mb-4 text-3xl font-bold">
                Simple, transparent pricing
              </Title>
              <Text className="text-lg text-gray-500">
                One plan, everything included. No per-seat charges.
              </Text>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              {/* Yearly */}
              <div className="relative flex flex-col rounded-2xl border border-primary bg-white p-8 shadow-md transition-all hover:shadow-lg">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="rounded-full bg-primary px-4 py-1 text-xs font-semibold text-white">
                    Recommended
                  </span>
                </div>

                <Title level={3} className="mb-1">
                  Yearly
                </Title>
                <Text className="mb-6 block text-gray-500">
                  Save 20% with annual billing
                </Text>

                <div className="mb-6">
                  <span className="text-5xl font-bold tracking-tight">
                    {pricing.currencySymbol}
                    {Math.round(pricing.standard * 0.8)}
                  </span>
                  <Text type="secondary" className="ml-2 text-lg">
                    per month, billed yearly
                  </Text>
                </div>

                {company.plan === 'standard' ? (
                  <Button size="large" className="mb-8 h-12 w-full" disabled>
                    Current Plan
                  </Button>
                ) : (
                  <Button
                    onClick={async () => {
                      const { url } = await api.createCheckoutSession('yearly')
                      window.location.href = url
                    }}
                    type="primary"
                    size="large"
                    className="mb-8 h-12 w-full"
                  >
                    <span className="flex items-center justify-center gap-2">
                      Upgrade Now
                      <ArrowRight className="h-5 w-5" />
                    </span>
                  </Button>
                )}

                <div className="space-y-3">
                  {features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                      <Text className="text-sm">{feature}</Text>
                    </div>
                  ))}
                </div>
              </div>

              {/* Monthly */}
              <div className="relative flex flex-col rounded-2xl border border-gray-200 bg-white p-8 shadow-sm transition-all hover:shadow-lg">
                <Title level={3} className="mb-1">
                  Monthly
                </Title>
                <Text className="mb-6 block text-gray-500">
                  Flexible month-to-month billing
                </Text>

                <div className="mb-6">
                  <span className="text-5xl font-bold tracking-tight">
                    {pricing.currencySymbol}
                    {pricing.standard}
                  </span>
                  <Text type="secondary" className="ml-2 text-lg">
                    per month
                  </Text>
                </div>

                {company.plan === 'standard' ? (
                  <Button size="large" className="mb-8 h-12 w-full" disabled>
                    Current Plan
                  </Button>
                ) : (
                  <Button
                    onClick={async () => {
                      const { url } = await api.createCheckoutSession('monthly')
                      window.location.href = url
                    }}
                    size="large"
                    className="mb-8 h-12 w-full"
                  >
                    <span className="flex items-center justify-center gap-2">
                      Upgrade Now
                      <ArrowRight className="h-5 w-5" />
                    </span>
                  </Button>
                )}

                <div className="space-y-3">
                  {features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                      <Text className="text-sm">{feature}</Text>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Text type="secondary">{pricing.subtitle}</Text>
            </div>
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="bg-white py-16">
          <div className="mx-auto max-w-6xl px-6">
            <div className="mb-12 text-center">
              <Title level={2} className="mb-4 text-3xl font-bold">
                Everything your studio needs
              </Title>
              <Text className="text-lg text-gray-500">
                Every feature is included - no hidden tiers or add-ons.
              </Text>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {featureHighlights.map((feature) => (
                <div
                  key={feature.title}
                  className="group rounded-2xl border border-gray-100 bg-gray-50 p-6 transition-all hover:border-primary/20 hover:bg-white hover:shadow-lg"
                >
                  <div className="group-hover:bg-primary/15 mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 transition-colors">
                    {React.cloneElement(feature.icon, {
                      className: 'text-primary'
                    })}
                  </div>
                  <Title
                    level={5}
                    className="mb-2 transition-colors group-hover:text-primary"
                  >
                    {feature.title}
                  </Title>
                  <Text className="text-sm text-gray-500">
                    {feature.description}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-gray-50 py-16">
          <div className="mx-auto max-w-3xl px-6">
            <div className="mb-12 text-center">
              <Title level={2} className="mb-4 text-3xl font-bold">
                Frequently Asked Questions
              </Title>
              <Text className="text-lg text-gray-500">
                Everything you need to know about pricing and plans
              </Text>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <Collapse
                  key={index}
                  items={[
                    {
                      key: String(index),
                      label: <Text strong>{faq.question}</Text>,
                      children: (
                        <Text className="text-gray-600">{faq.answer}</Text>
                      )
                    }
                  ]}
                />
              ))}
            </div>
          </div>
        </div>
      </Content>
    </Layout>
  )
}
